import * as React from 'react'
require('../base.module.scss')
import * as cn from 'classnames'
import * as st from './modal.module.scss'

import { BaseComponentProps } from '../Common/extractClassNamesFromProps'
import { extractClassNamesFromProps } from '../Common/extractClassNamesFromProps'
import { Icon, IconType } from '../Icon/Icon'

/**
 * @param isVisible Sets the visibility of the modal
 * @param header    The header content of the modal
 * @param footer    The footer content of the modal
 * @param onClose   Function to be called upon modal closure
 * @param modalId   The modalId. This has to be defined when more than 1 modal are rendered, in order to avoid
 *                  having actions on one modal affect the other (e.g. AVWebDetailPage-3378)
 */
interface ModalProps extends BaseComponentProps {
  isVisible?: boolean,
  header?: React.ReactChild,
  footer?: React.ReactChild,
  onClose?: (() => any)
  modalId?: string
}
interface State {
  isVisible: boolean
}

const LOCK_CLASSNAME = 'dvui-no-scroll'

export class Modal extends React.Component<ModalProps, State> {
  // Add displayName to preserve the component name on snapshot testing
  displayName = 'Modal'

  static defaultProps = {
    isVisible: false as boolean,
    header: false as boolean,
    footer: false as boolean,
    onClose: (() => { }) as Function,
    modalId: 'dvui-close-overlay' as string
  }
  constructor(props: ModalProps) {
    super(props)
    this.state = {
      isVisible: Boolean(props.isVisible)
    }
  }
  selectableElements = 'a[href],textarea,input,button,label'
  modalRef = React.createRef<HTMLDivElement>()

  componentWillUnmount() {
    this.removeLock()
  }

  focusOnVisible() {
    if (!this.modalRef.current) {
      return
    }
    document.documentElement.classList.add(LOCK_CLASSNAME)
    this.modalRef.current.tabIndex = 0
    this.modalRef.current.focus()
    this.modalRef.current.tabIndex = -1
    this.modalRef.current.parentElement!.scrollTop = 0
  }

  removeLock() {
    document.documentElement.classList.remove(LOCK_CLASSNAME)
  }

  componentDidUpdate(_prevProps: ModalProps, prevState: State) {
    if (document && document.documentElement && prevState.isVisible !== this.state.isVisible) {
      if (this.state.isVisible) {
        this.focusOnVisible()
      } else {
        this.removeLock()
        this.props.onClose!()
      }
    }
  }

  componentDidMount() {
    if (document && document.documentElement) {
      if (this.state.isVisible) {
        this.focusOnVisible()
      } else {
        this.removeLock()
      }
    }
  }

  public open = () => {
    this.setState({ isVisible: true })
  }

  public close = () => {
    this.setState({ isVisible: false })
  }

  public toggle = () => {
    this.setState({ isVisible: !this.state.isVisible })
  }

  maintainFocus = (e: React.KeyboardEvent) => {
    if (!this.modalRef.current) {
      return
    }
    if (document && document.activeElement && e.key === 'Tab') {
      if (document.activeElement.classList.contains('dvui-modal-end')) {
        (this.modalRef.current.querySelector(this.selectableElements) as HTMLElement).focus()
      } else if (document.activeElement.classList.contains('dvui-modal-start')) {
        const allSubElms = this.modalRef.current.querySelectorAll(this.selectableElements) as NodeListOf<HTMLElement>
        allSubElms[allSubElms.length - 1].focus()
      }
    }
  }

  render() {
    return (
      <>
        <input className={cn(st.hidden, 'dvui-modal-hidden')}
          aria-hidden={true}
          id={this.props.modalId}
          type="checkbox"
          checked={this.state.isVisible}
          onChange={this.toggle}
        />
        <div className={cn(st.overlay, 'dvui-modal-overlay')}
          aria-hidden={!this.state.isVisible}
          onKeyUp={this.maintainFocus}
          onKeyDown={this.maintainFocus}>
          <a className={cn(st.focusElements, 'dvui-modal-start')}
            aria-hidden={true}
            tabIndex={0} />
          <label className={cn(st.closeOverlay, 'dvui-modal-closeOverlay')}
            htmlFor={this.props.modalId} />
          <div className={extractClassNamesFromProps(this.props, st.modal, 'dvui-modal')}
            aria-live={this.state.isVisible ? 'polite' : 'off'}
            ref={this.modalRef}>
            <div className={cn(st.headerContainer, 'dvui-modal-header')}>
              {this.props.header &&
                <header className={cn(st.header, 'dvui-modal-header')}>
                  {this.props.header}
                </header>
              }
              <label className={cn(st.closeButton, 'dvui-modal-closeButton')}
                htmlFor={this.props.modalId}
                tabIndex={0}>
                <Icon use={IconType.DISMISSABLE_CLOSE} />
              </label>
            </div>
            <div className={cn('dvui-modal-content')}>
              {this.props.children}
            </div>
            {this.props.footer &&
              <footer className={cn(st.footer, 'dvui-modal-footer')}>
                {this.props.footer}
              </footer>
            }
            <a className={cn(st.focusElements, 'dvui-modal-end')}
              aria-hidden={true}
              tabIndex={0} />
          </div>
        </div>
      </>
    )
  }
}
